import React from "react";
import { Menu } from "@headlessui/react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import Tooltip from "components/Tooltip";

export interface ButtonMenuItem {
  name: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface ButtonMenuProps {
  items: ButtonMenuItem[];
  loading: boolean;
  disabled?: boolean;
  current?: ButtonMenuItem;
  tooltip?: string;
  className?: string;
  buttonClassName?: string;
}

export default function ButtonMenu(props: ButtonMenuProps) {
  return (
    <Tooltip content={props.tooltip ?? ""}>
      <div
        className={clsx(
          "inline-flex rounded-md",
          {
            "opacity-50 pointer-events-none": props.loading,
          },
          props.className
        )}
      >
        <button
          type="button"
          className={clsx(
            "relative inline-flex items-center rounded-l-md px-3 py-2 text-sm",
            "font-semibold text-white",
            "bg-indigo-600 hover:bg-indigo-700 shadow-md",
            { "opacity-50 pointer-events-none": props.disabled }
          )}
          onClick={props.items[0].onClick}
          disabled={props.loading}
        >
          {props.current?.name || props.items[0].name}
          {props.loading && (
            <svg
              className="animate-spin ml-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
        </button>
        <Menu as="div" className="relative -ml-px block">
          <Menu.Button
            className={clsx(
              "relative inline-flex items-center rounded-r-md px-2 py-2 border-l border-white",
              "bg-indigo-600 hover:bg-indigo-700 shadow-md text-white",
              props.buttonClassName
            )}
          >
            <span className="sr-only">Open options</span>
            <IconChevronDown aria-hidden="true" className="size-6" />
          </Menu.Button>
          <Menu.Items
            className={clsx(
              "absolute right-0 z-10 mt-1 w-56 rounded-md bg-white transition",
              "data-enter:ease-out data-leave:duration-75 data-leave:ease-in border border-gray-300"
            )}
          >
            <div className="py-1">
              {props.items
                .filter((item) => item.name !== props.items[0].name)
                .map((item) => (
                  <Menu.Item key={item.name}>
                    <div
                      className={clsx(
                        "block px-4 py-2 text-sm data-focus:bg-white/5 hover:bg-indigo-50 text-gray-800",
                        "data-focus:text-white data-focus:outline-hidden cursor-pointer",
                        { "opacity-50 pointer-events-none": item.disabled }
                      )}
                      onClick={item.onClick}
                    >
                      {item.name}
                    </div>
                  </Menu.Item>
                ))}
            </div>
          </Menu.Items>
        </Menu>
      </div>
    </Tooltip>
  );
}
